import React, { useState, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { motion } from "framer-motion";
import { GripVertical, Sparkles, Palette, ChevronDown, ChevronUp, Wand2 } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import BackToDashboardButton from "@/components/BackToDashboardButton";

const TYPE_COLORS = {
  epreuve: "bg-red-100 text-red-800",
  reussite: "bg-green-100 text-green-800",
  tournant: "bg-blue-100 text-blue-800",
  rencontre: "bg-purple-100 text-purple-800",
  apprentissage: "bg-amber-100 text-amber-800",
  passion: "bg-pink-100 text-pink-800",
};

function SortableStoryCard({ story, isSelected, onToggle, note, onNoteChange }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: story.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Card className={`mb-3 transition-all ${isSelected ? "border-primary/50 shadow-sm" : "opacity-60"}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Drag handle */}
            <button
              className="mt-1 cursor-grab touch-none text-muted-foreground hover:text-foreground"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-5 w-5" />
            </button>

            {/* Checkbox */}
            <Checkbox
              checked={isSelected}
              onCheckedChange={onToggle}
              className="mt-1"
            />

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-medium text-sm">{story.titre}</span>
                {story.periode && (
                  <Badge variant="outline" className="text-xs">
                    {story.periode}
                  </Badge>
                )}
                {story.type && (
                  <Badge
                    className={`text-xs border-0 ${TYPE_COLORS[story.type] || "bg-gray-100 text-gray-800"}`}
                  >
                    {story.type}
                  </Badge>
                )}
              </div>

              {story.resume && (
                <p className="text-sm text-muted-foreground mb-2">{story.resume}</p>
              )}

              {/* Elements cles */}
              {story.elements_cles && story.elements_cles.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {story.elements_cles.map((el, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs font-normal">
                      {el}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Notes textarea (visible when selected) */}
              {isSelected && (
                <Textarea
                  placeholder="Notes optionnelles pour cette histoire..."
                  value={note}
                  onChange={(e) => onNoteChange(story.id, e.target.value)}
                  className="mt-2 text-sm min-h-[60px]"
                  rows={2}
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function StoriesScreen({
  stories,
  style,
  onGenerate,
  onBackToDashboard,
  isLoading,
  promptVariants = [],
  selectedPromptVariantId,
  onPromptVariantChange,
  defaultPromptVariantId = "classique",
}) {
  const [orderedIds, setOrderedIds] = useState(() =>
    stories.map((s) => s.id)
  );
  const [selectedIds, setSelectedIds] = useState(() =>
    new Set(stories.map((s) => s.id))
  );
  const [notes, setNotes] = useState({});
  const [editingStyle, setEditingStyle] = useState(false);
  const [styleJson, setStyleJson] = useState(() =>
    style ? JSON.stringify(style, null, 2) : "{}"
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const storiesMap = useMemo(() => {
    const map = {};
    stories.forEach((s) => { map[s.id] = s; });
    return map;
  }, [stories]);

  const orderedStories = useMemo(
    () => orderedIds.map((id) => storiesMap[id]).filter(Boolean),
    [orderedIds, storiesMap]
  );

  const selectedCount = selectedIds.size;
  const totalCount = stories.length;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setOrderedIds((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleNoteChange = (id, value) => {
    setNotes((prev) => ({ ...prev, [id]: value }));
  };

  const handleGenerate = () => {
    let parsedStyle = style;
    if (editingStyle) {
      try {
        parsedStyle = JSON.parse(styleJson);
      } catch {
        // Keep original style if JSON is invalid
        parsedStyle = style;
      }
    }

    const selectedOrderedIds = orderedIds.filter((id) => selectedIds.has(id));

    onGenerate({
      selected_story_ids: selectedOrderedIds,
      story_order: selectedOrderedIds,
      notes,
      style: parsedStyle,
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {onBackToDashboard && (
          <div className="mb-4">
            <BackToDashboardButton onClick={onBackToDashboard} />
          </div>
        )}

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-2xl font-bold mb-2">Vos histoires</h1>
          <p className="text-muted-foreground">
            Sélectionnez, réordonnez et annotez les histoires avant la rédaction.
          </p>
        </motion.div>

        {/* Prompt variant selector */}
        {promptVariants.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <Card className="mb-6">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Wand2 className="h-5 w-5 text-primary" />
                  <CardTitle className="text-base">Style de rédaction</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={selectedPromptVariantId || defaultPromptVariantId}
                  onValueChange={onPromptVariantChange}
                  className="space-y-2"
                >
                  {promptVariants.map((variant) => (
                    <label
                      key={variant.id}
                      htmlFor={`variant-${variant.id}`}
                      className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                        (selectedPromptVariantId || defaultPromptVariantId) === variant.id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      }`}
                    >
                      <RadioGroupItem
                        value={variant.id}
                        id={`variant-${variant.id}`}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Label
                            htmlFor={`variant-${variant.id}`}
                            className="font-medium text-sm cursor-pointer"
                          >
                            {variant.nom}
                          </Label>
                          {variant.id === defaultPromptVariantId && (
                            <Badge variant="outline" className="text-[10px] font-normal">
                              Par défaut
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                          {variant.description}
                        </p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
                <p className="text-xs text-muted-foreground mt-3">
                  Vous pourrez régénérer avec un autre style depuis l'écran suivant.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Style card */}
        {style && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="mb-6">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Palette className="h-5 w-5 text-primary" />
                    <CardTitle className="text-base">Ton détecté</CardTitle>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingStyle(!editingStyle)}
                  >
                    {editingStyle ? (
                      <>Fermer <ChevronUp className="h-4 w-4 ml-1" /></>
                    ) : (
                      <>Modifier <ChevronDown className="h-4 w-4 ml-1" /></>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {!editingStyle ? (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {style.registre && (
                      <div>
                        <span className="text-muted-foreground">Registre :</span>{" "}
                        <span className="font-medium">{style.registre}</span>
                      </div>
                    )}
                    {style.rythme && (
                      <div>
                        <span className="text-muted-foreground">Rythme :</span>{" "}
                        <span className="font-medium">{style.rythme}</span>
                      </div>
                    )}
                    {style.expressions_recurrentes && style.expressions_recurrentes.length > 0 && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Expressions :</span>{" "}
                        <span className="font-medium">{style.expressions_recurrentes.join(", ")}</span>
                      </div>
                    )}
                    {style.specificites && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Spécificités :</span>{" "}
                        <span className="font-medium">{style.specificites}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <Textarea
                    value={styleJson}
                    onChange={(e) => setStyleJson(e.target.value)}
                    className="font-mono text-xs min-h-[150px]"
                    rows={8}
                  />
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Counter */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="flex items-center justify-between mb-4"
        >
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{selectedCount}</span>{" "}
            histoires sélectionnées sur{" "}
            <span className="font-semibold text-foreground">{totalCount}</span>{" "}
            détectées
          </p>
        </motion.div>

        {/* Stories list */}
        <ScrollArea className="h-[calc(100vh-420px)] pr-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
              {orderedStories.map((story, index) => (
                <SortableStoryCard
                  key={story.id}
                  story={story}
                  isSelected={selectedIds.has(story.id)}
                  onToggle={() => toggleSelection(story.id)}
                  note={notes[story.id] || ""}
                  onNoteChange={handleNoteChange}
                />
              ))}
            </SortableContext>
          </DndContext>
        </ScrollArea>

        {/* Generate button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-6"
        >
          <Button
            className="w-full"
            size="lg"
            onClick={handleGenerate}
            disabled={selectedCount === 0 || isLoading}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {isLoading
              ? "Génération en cours..."
              : `Générer avec ${selectedCount} histoire${selectedCount > 1 ? "s" : ""}`}
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
